import React, { useState, useEffect } from "react";
import ReportHeader from "./ReportHeader";
import { adminServices } from "../../../services/AdminServices";

const formatCurrency = (v) =>
  v != null && !isNaN(Number(v)) ? `PHP${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const DUMMY_ROWS = [];

const YearToDateSalesReport = ({ onBack, dashboard, metrics, backButtonIcon, facilityId = null }) => {
  const [apiRows, setApiRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchYearToDateSales = async () => {
      setLoading(true);
      setError(null);
      try {
        let endpoint = "dashboard/yeartodatesales";

        if(facilityId != null || facilityId != "" || facilityId != undefined){
          endpoint += `?facilityId=${facilityId}`;
        }
        const response = await adminServices.getData(endpoint);
        const data = response?.data?.data ?? response?.data ?? [];
        if (!cancelled) {
          setApiRows(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching Year to Date Sales:", err);
        if (!cancelled) {
          setError(err);
          setApiRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchYearToDateSales();

    return () => {
      cancelled = true;
    };
  }, []);

  const dashboardRaw = dashboard?.yearToDateSalesDetails ?? dashboard?.yearToDateSalesList ?? (Array.isArray(dashboard?.yearToDateSales) ? dashboard.yearToDateSales : []);
  const raw = apiRows.length > 0 ? apiRows : dashboardRaw;

  const fromApi = (Array.isArray(raw) ? raw : []).map((r, idx) => {
    const amount = r.amount ?? r.total ?? r.value ?? 0;
    return {
      id: r.id ?? idx + 1,
      date: r.date ?? r.orderDate ?? r.transactionDate ?? "",
      merchantName: r.merchantName ?? r.merchant ?? "",
      orderType: r.orderType ?? r.type ?? "",
      facility: r.facility ?? r.facilityName ?? r.colonyName ?? "",
      amount: typeof amount === "number" ? amount : Number(amount) || 0,
      orderStatus: r.orderStatus ?? r.status ?? "Completed"
    };
  });
  const rows = fromApi.length > 0 ? fromApi : DUMMY_ROWS;
  const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const displayTotal = metrics?.yearToDateSales ?? total;

  if (loading) {
    return (
      <div className="dashboard-content">
        <ReportHeader onBack={onBack} breadcrumbLabel="Year to date Sales" backButtonIcon={backButtonIcon} />
        <div style={{ padding: 30, textAlign: "center", color: "#6b7280" }}>Loading Year to Date Sales data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <ReportHeader onBack={onBack} breadcrumbLabel="Year to date Sales" backButtonIcon={backButtonIcon} />
        <div style={{ padding: 30, textAlign: "center", color: "#b91c1c" }}>
          Failed to load data. Please refresh.
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <ReportHeader onBack={onBack} breadcrumbLabel="Year to date Sales" backButtonIcon={backButtonIcon} />
      <div style={{ backgroundColor: "#ffffff", borderRadius: 12, padding: 24, marginTop: 24, boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 16 }}>
          <div>
            <h1 className="dashboard-title" style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700, color: "#111827" }}>Year to date Sales</h1>
            <p style={{ margin: 0, marginTop: 4, fontSize: 13, color: "#6b7280" }}>Complete sales overview for {new Date().getFullYear()}</p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", justifyContent: "flex-end" }}>
            <div style={{ backgroundColor: "#BCE9FF", padding: "12px 20px", borderRadius: 8, minWidth: 180, maxWidth: 200, textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#0369a1", marginBottom: 4 }}>Total Sales (YTD)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(displayTotal) ?? "PHP0.00"}</div>
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table style={{ minWidth: 800, width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ backgroundColor: "#0F2A40", color: "#ffffff" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Date</th>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Merchant Name</th>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Order Type</th>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Facilities</th>
                <th style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>Amount (PHP)</th>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Order Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "16px 12px", textAlign: "center", color: "#6b7280" }}>No records</td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => {
                    const status = String(row.orderStatus || "").toLowerCase();
                    const isCompleted = status === "completed";
                    const badgeStyle = {
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 500,
                      backgroundColor: isCompleted ? "#dcfce7" : "#fef3c7",
                      color: isCompleted ? "#166534" : "#b45309"
                    };
                    return (
                      <tr key={row.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "10px 12px" }}>{row.date}</td>
                        <td style={{ padding: "10px 12px" }}>{row.merchantName}</td>
                        <td style={{ padding: "10px 12px" }}>{row.orderType}</td>
                        <td style={{ padding: "10px 12px" }}>{row.facility}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatCurrency(row.amount)}</td>
                        <td style={{ padding: "10px 12px" }}><span style={badgeStyle}>{row.orderStatus || "—"}</span></td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={4} style={{ padding: "12px", textAlign: "right", fontWeight: 600, borderTop: "1px solid #e5e7eb" }}>Total:</td>
                    <td style={{ padding: "12px", textAlign: "right", fontWeight: 700, borderTop: "1px solid #e5e7eb" }}>{formatCurrency(total)}</td>
                    <td style={{ padding: "12px", borderTop: "1px solid #e5e7eb" }} />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default YearToDateSalesReport;
